/**
 * Читалка · Фаза 3 окна «книги» — ЧИСТАЯ ЛОГИКА, без DOM.
 *
 * Здесь живёт всё, что можно проверить запуском: разбор абзаца на слова и промежутки,
 * правила подсветки, что тапается, а что нет, тексты карточки слова и строки прогресса.
 * Раскладка страницы (набор «пока влезает») — в экране: она про размеры, её проверяет
 * живой стенд.
 *
 * ⛔ Ни одной своей цифры: покрытие, проценты, «осталось минут» и разбор слов приезжают
 * от ядра. Витрина решает только, какими словами это сказать.
 */

import type { BookWord } from "./api";

/** Кегль книжной страницы. Числа — те же, что в принятом прототипе 22.08. */
export const SIZE_PX: Record<string, number> = { s: 15, m: 17, l: 19 };
export type ReaderSize = "s" | "m" | "l";
export type Highlight = "off" | "learn" | "all";

/** Слово текста: буквы, апостроф внутри и ⭐ апостроф в конце (`th'`, `goin'`). */
const WORD = /[A-Za-z]+(?:['’][A-Za-z]+)?['’]?/g;

export interface Piece { text: string; word: boolean; key?: string }

/**
 * Абзац → куски: слова и всё, что между ними (пробелы, знаки препинания, тире).
 *
 * ⚠ Промежутки возвращаются ОТДЕЛЬНЫМИ кусками, а не приклеиваются к слову: строка на
 * странице должна рваться там, где решит браузер, а склейка «слово+запятая» ломает перенос.
 */
export function pieces(paragraph: string): Piece[] {
  const out: Piece[] = [];
  let last = 0;
  WORD.lastIndex = 0;
  for (let m = WORD.exec(paragraph); m; m = WORD.exec(paragraph)) {
    if (m.index > last) out.push({ text: paragraph.slice(last, m.index), word: false });
    out.push({ text: m[0], word: true, key: normalize(m[0]) });
    last = m.index + m[0].length;
  }
  if (last < paragraph.length) out.push({ text: paragraph.slice(last), word: false });
  return out;
}

/** Написание → ключ словаря страницы (тот же нижний регистр, что у ядра). */
export function normalize(word: string): string {
  return word.toLowerCase().replace(/’/g, "'");
}

/**
 * Тапается ли слово. ⛔ Имя собственное — нет: это не слово языка, и карточки у него
 * не будет (решение плана 22.08). ⛔ Буква (инициал `L.`, «the letter M») — тоже нет.
 */
export function tappable(key: string, names: Set<string>): boolean {
  if (!key || names.has(key)) return false;
  return key.length > 1 || key === "a" || key === "i";
}

/** Учит ли человек это слово прямо сейчас (для подсветки «что учу»). */
export function isLearning(w: BookWord | undefined): boolean {
  if (!w || !w.known) return false;
  return (w.box ?? 0) >= 1 || w.status === "learning" || w.status === "known";
}

/**
 * Класс подсветки слова. ⭐ По умолчанию подсветки НЕТ: замер прототипом показал, что
 * при «подсветить всё» подчёркнуто ~89 % слов и страница перестаёт быть страницей.
 */
export function wordClass(w: BookWord | undefined, mode: Highlight): string {
  const base = "w";
  if (mode === "off") return base;
  if (!w || !w.known) return mode === "all" ? `${base} miss` : base;
  if (isLearning(w)) return `${base} learn`;
  return mode === "all" ? `${base} fresh` : base;
}

/* ── тексты карточки слова ─────────────────────────────────────────────────── */

const POS_RU: Record<string, string> = {
  noun: "существительное", verb: "глагол", adjective: "прилагательное",
  adverb: "наречие", pronoun: "местоимение", preposition: "предлог",
  conjunction: "союз", determiner: "определитель", numeral: "числительное",
  phrase: "выражение", interjection: "междометие",
};

export const posLabel = (pos?: string | null): string =>
  (pos && POS_RU[pos.toLowerCase()]) || "";

/**
 * Состояние слова у человека. ⛔ «Учите» и «ещё не начинали» — не одно и то же:
 * первое честно называет коробку, второе не притворяется прогрессом.
 */
export function stateLabel(w: BookWord): { text: string; kind: string } {
  if (!w.known) return { text: "нет в словаре", kind: "miss" };
  if ((w.box ?? 0) >= 1 || w.status === "known") {
    return { text: `учите · коробка ${w.box ?? 1}`, kind: "learn" };
  }
  return { text: "ещё не начинали", kind: "new" };
}

/**
 * ⚠ Строка «начальная форма» показывается ТОЛЬКО когда на странице стоит форма.
 * Дефект, пойманный прототипом: под словом `beginning` стояла транскрипция `/bɪˈɡɪn/` —
 * то есть чужая. Теперь транскрипция уезжает вниз, к начальной форме, которой она и
 * принадлежит.
 */
export function showsLemma(w: BookWord): boolean {
  return Boolean(w.known && w.is_form && w.lemma);
}

/** Показывать ли транскрипцию рядом с самим словом страницы. */
export function ipaAtWord(w: BookWord): boolean {
  return Boolean(w.known && w.ipa && !showsLemma(w));
}

/** Текст объяснения для слова, которого нет в словаре. */
export const MISSING_NOTE =
  "Этого слова нет в нашем словаре. В подготовленной книге такого быть не должно: " +
  "недостающие слова добавляются один раз, до того как книгу увидит первый читатель.";

/* ── строки прогресса ──────────────────────────────────────────────────────── */

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export const wordsLabel = (n: number) =>
  `${n.toLocaleString("ru-RU")} ${plural(n, "слово", "слова", "слов")}`;

export const minutesLabel = (n: number) =>
  `${n} ${plural(n, "минута", "минуты", "минут")}`;

/** «Прочитано 12 %» / «не начата» — ⛔ без «0 %», которое читается как поломка. */
export function progressLabel(row: { started: boolean; percent: number }): string {
  if (!row.started) return "не начата";
  if (row.percent <= 0) return "только начали";
  if (row.percent >= 100) return "прочитана";
  return `прочитано ${row.percent} %`;
}

/** «Осталось 214 слов ≈ 2 минуты» — обе цифры от ядра. */
export function leftLabel(row: { words_left: number; minutes_left: number }): string {
  if (row.words_left <= 0) return "дочитана до конца";
  return `осталось ${wordsLabel(row.words_left)} ≈ ${minutesLabel(row.minutes_left)}`;
}

/** «Страница 3 из 48». Номер человеческий, с единицы. */
export const pageLabel = (cur: number, total: number) =>
  `${Math.min(cur + 1, Math.max(total, 1))} / ${Math.max(total, 1)}`;

/**
 * Покрытие книги словами приложения. ⚠ Называем вслух, что это значит: «тап сработает».
 * ⛔ Ярлыка «подходит / не подходит» не ставим — личную цифру ещё нечем считать честно.
 */
export function coverageLabel(coverage: number | null | undefined): string {
  if (coverage === null || coverage === undefined) return "покрытие пока не считали";
  // ⚠ Дробь по-русски пишется через запятую — точка на экране читается как чужая.
  const pct = (coverage * 100).toFixed(1).replace(".", ",");
  return `${pct} % слов книги приложение объяснит`;
}

/** Длина фразы — та самая ось, по которой книги стоят на полке лестницей. */
export const sentenceLabel = (n: number | null | undefined) =>
  n ? `фраза ${n} ${plural(n, "слово", "слова", "слов")}` : "";
